import {
    clientErrorMessages,
    EMPTY_STRING,
    refFrames,
    stepPath,
    WEBSOCKET_API_ENDPOINT,
} from "../../constants";
import * as TreatmentPlan from "../../helpers/tplan/tplan";
import {
    cpmErrorCodes,
    cpmErrorMessages,
    cpmStatusCodes,
    generateCPMLoadStates,
    rawCPMFileName,
    WEBSOCKET_TIMEOUT_MESSAGE,
    webSocketActions,
} from "./constants";
import { addPendoTracking } from "../../helpers/helpers";
import { trackingEventNames } from "../../helpers/pendo_constants";

/**
 * This API generates a cancer map and the margin meshes for a patient plan.
 * The API uses a web socket. On opening the web socket, a request body containing
 * parameters for CEM generation and margin generation are included.
 *
 * When the request is received on the backend, response messages are sent back through
 * the web socket. The response messages will include the status and progress of the API.
 * When the API has completed its work successfully, it will send a final message through
 * the web socket that includes all the render parameters for the cancer map. This final
 * message does not include the progress amount.
 *
 * A web socket implementation was chosen due to its ability to communicate ongoing status
 * changes.
 */
export function generateCPM() {
    const socket = new WebSocket(WEBSOCKET_API_ENDPOINT);

    let {
        useruuid,
        patientuuid,
        planuuid,
        visituuid,
        authToken: token,
        currentTreatmentPlan,
    } = this.props;
    let plan = currentTreatmentPlan.TreatmentPlan;
    let outputURI = TreatmentPlan.get3DDataFolder(
        currentTreatmentPlan,
        patientuuid,
        visituuid
    );

    socket.onopen = () => {
        socket.send(
            JSON.stringify({
                action: webSocketActions.CEM_GENERATION_WITH_UPDATE_PLAN,
                body: {
                    cem_generation_request_body: {
                        prostateURI: TreatmentPlan.getStructureInFrame(
                            TreatmentPlan.getCoreStructure(plan),
                            refFrames.MRI
                        ).URI,
                        roiURIs: TreatmentPlan.getROIs(plan).map((r) => {
                            return r.Frame[refFrames.MRI].URI;
                        }),
                        mriURIs: TreatmentPlan.getMRI(plan)[0].Slices.map(
                            (s) => {
                                return s.URI;
                            }
                        ),
                        bxCores: plan.StructureData.Biopsy,
                        psa: plan.PatientData.PSA,
                        stride: 2,
                        outputURI: outputURI,
                        useruuid: useruuid,
                        patientuuid: patientuuid,
                        planuuid: planuuid,
                        authToken: token,
                    },
                    margin_generation_request_body: {
                        prostateURI: TreatmentPlan.getStructureInFrame(
                            TreatmentPlan.getCoreStructure(
                                this.props.currentTreatmentPlan.TreatmentPlan
                            ),
                            refFrames.MRI
                        ).URI,
                        cpmURI: `${outputURI}/CPM/${rawCPMFileName}`,
                        searchForOptimalCPM: true,
                    },
                },
            })
        );
    };

    socket.onmessage = (event) => {
        let data = JSON.parse(event.data);

        if (data.message === WEBSOCKET_TIMEOUT_MESSAGE) return;

        if (
            data.body.message &&
            data.body.message === cpmStatusCodes.FAILURE &&
            data.body.payload.status !==
                cpmStatusCodes.CALCULATE_TUMOR_VOLUME_FAILED &&
            data.body.payload.status !==
                cpmStatusCodes.CALCULATE_CAPSULE_RISK_SCORES_FAILED
        ) {
            this.setState({
                cemError: {
                    errorCode: cpmErrorCodes.NETWORK_ERROR,
                    errorDetails: cpmErrorMessages.NETWORK_ERROR,
                },
                loadState: generateCPMLoadStates.CEM_FAILED,
            });
            addPendoTracking(
                trackingEventNames.CEM,
                planuuid,
                clientErrorMessages.CPM_GENERATION_FAILED,
                { cemStatus: cpmStatusCodes.FAILURE }
            );
            socket.close();
            return;
        }

        if (
            data.body.payload.status ===
            cpmStatusCodes.TREATMENT_PLAN_UPDATE_COMPLETE
        ) {
            this.setState({
                loadState: generateCPMLoadStates.CPM_COMPLETE,
            });
            addPendoTracking(trackingEventNames.CEM, planuuid, null, {
                cemStatus: cpmStatusCodes.TREATMENT_PLAN_UPDATE_COMPLETE,
            });
            socket.close();
            return;
        }

        if (data.body.payload.progress) {
            this.setState({
                mapProgressMessages: [
                    ...this.state.mapProgressMessages,
                    data.body.payload.progress,
                ],
                requestUuid: data.body.payload.progress.requestUUID,
            });
        }

        if (
            data.body.payload.progress &&
            data.body.payload.progress.errorCode &&
            data.body.payload.progress.errorCode !== EMPTY_STRING
        ) {
            this.setState({
                cemError: {
                    errorCode: data.body.payload.progress.errorCode,
                    errorDetails: data.body.payload.progress.errorDetails,
                    requestUuid: this.state.requestUuid,
                    timestamp: data.body.payload.progress.timestamp,
                },
                loadState: generateCPMLoadStates.CEM_FAILED,
            });
            addPendoTracking(
                trackingEventNames.CEM,
                planuuid,
                clientErrorMessages.CPM_GENERATION_FAILED,
                {
                    cemStatus: cpmStatusCodes.CEM_GENERATION_FAILED,
                    errorCode: data.body.payload.progress.errorCode,
                    errorDetails: data.body.payload.progress.errorDetails,
                }
            );
            socket.close();
        }
    };

    /**
     * CEM and Margin Generation Notes
     *
     * Current workflow when the websocket closes:
     * - On success: Navigate to Step 4
     * - On failure: Stay in Step 4_1 and display error. When user resumes this plan, they will see an error page that
     *               guides them back to Step 3 to regenerate the CEM.
     *
     *  Note: Currently, the backend updates planStage to CEM_GENERATED on cem/margin generation success, while frontend
     *  updates planStage to CEM_FAILED on cem/margin generation failure via updateTreatmentPlanWithCEMFailure().
     *
     *  TODO: Future work should consolidate planStage updates to be handled by the backend websocket.
     */
    socket.onclose = () => {
        if (this.state.loadState === generateCPMLoadStates.CPM_COMPLETE) {
            this.props.history.push(stepPath.STEP4);
            addPendoTracking(trackingEventNames.CEM, planuuid, null, {
                cemStatus: cpmStatusCodes.CANCER_MAP_GENERATION_COMPLETE,
            });
        }
        if (this.state.loadState === generateCPMLoadStates.CEM_FAILED) {
            this.updateTreatmentPlanWithCEMFailure();
        }
    };
}
